import { useId, useState } from 'react';
import {
  Box,
  Button,
  ButtonGroup,
  Form,
  Stack,
  Text,
  TextArea,
  TextField,
  useSnackbars,
} from '@customerio/pluma-components/react';
import { PageTitle } from '@/components/page-title';
import { useServicesMutation, useQueryClient } from '@/services/api';
import { isAPIError } from '@/services/api/ts-rest';
import { useAuth } from '@/services/auth';
import { useSafeNavigate } from '@/services/router/safe-navigate';
import { generatePath } from '@/services/router/generate-path';
import { useWorkspaceIdFromCurrentUrl } from '@/utils/workspace-id';
import { isEmptyString } from '@/utils/string';
import { useSkills, toFileName, isContentOverLimit } from '../hooks/use-skills';
import { AgentLayout } from '../../layout';

export function NewSkillPage() {
  const { account } = useAuth();
  const queryClient = useQueryClient();
  const snackbars = useSnackbars();
  const safeNavigate = useSafeNavigate();
  const formId = useId();
  const workspaceId = useWorkspaceIdFromCurrentUrl() ?? 0;

  const skillsListPath = generatePath('/workspaces/:workspaceId/agent/skills', { workspaceId });

  const { skills, maxSkills, isLoading: isSkillsLoading } = useSkills();

  const [name, setName] = useState('');
  const [content, setContent] = useState('');

  const fileName = toFileName(name);
  const filePath = `skills/${fileName}.md`;

  const saveMutation = useServicesMutation('updateCopilotFile', {
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['listCopilotFiles'] });
      snackbars.create({ variant: 'success', message: 'Skill created.' });
      safeNavigate(
        generatePath('/workspaces/:workspaceId/agent/skills/:skillPath', {
          workspaceId,
          skillPath: encodeURIComponent(`${fileName}.md`),
        }),
      );
    },
    onError: (error) => {
      let message = 'Failed to create skill. Please try again.';
      if (isAPIError(error) && error.response.status === 400) {
        const body = error.response.body as { errors?: Array<{ detail?: string }> } | undefined;
        const detail = body?.errors?.[0]?.detail;
        if (typeof detail === 'string' && detail.length > 0) {
          message = detail;
        }
      }
      snackbars.create({ variant: 'error', message });
    },
  });

  const isNameEmpty = isEmptyString(name);
  const isFileNameEmpty = isEmptyString(fileName);
  const isDuplicate = !isFileNameEmpty && skills.some((s) => s.fileName === `${fileName}.md`);
  const isContentEmpty = isEmptyString(content);
  const isContentTooLong = isContentOverLimit(content);
  const canSave =
    !isSkillsLoading &&
    !isNameEmpty &&
    !isFileNameEmpty &&
    !isDuplicate &&
    !isContentEmpty &&
    !isContentTooLong &&
    skills.length < maxSkills &&
    !saveMutation.isPending;

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!canSave) return;

    saveMutation.mutate({
      params: { accountId: account.id, filePath },
      body: { content },
    });
  };

  const remaining = maxSkills - skills.length;

  return (
    <AgentLayout title="New skill">
      <PageTitle title="Skills - New" />
      <Box overflow="auto" flexGrow="1">
        <Box display="flex" flexDirection="column" gap="300" w="full" p="300">
          {Number.isFinite(maxSkills) ? (
            <Text size="xs" color="subtle">
              {remaining > 0
                ? `${String(remaining)} of ${String(maxSkills)} skills remaining on your plan.`
                : `You've used all ${String(maxSkills)} skills on your plan.`}
            </Text>
          ) : null}
          <Form id={formId} onSubmit={handleSubmit}>
            <Stack gap="200">
              <TextField
                label="Name"
                value={name}
                onInput={(event: Event & { target: HTMLInputElement }) => setName(event.target.value)}
                isDisabled={saveMutation.isPending}
                isInvalid={(!isNameEmpty && isFileNameEmpty) || isDuplicate}
                error={
                  isDuplicate
                    ? 'A skill with this name already exists'
                    : !isNameEmpty && isFileNameEmpty
                    ? 'Name must contain at least one valid character'
                    : undefined
                }
              />
              <Stack gap="050">
                <TextArea
                  label="Content"
                  value={content}
                  onInput={(event: Event & { target: HTMLTextAreaElement }) => setContent(event.target.value)}
                  isDisabled={saveMutation.isPending}
                  minRows={8}
                  maxRows={24}
                  isInvalid={isContentTooLong}
                  error={isContentTooLong ? `Content exceeds maximum length of 1 MB` : undefined}
                  description="The instructions and knowledge this skill provides to the agent."
                />
              </Stack>
            </Stack>
          </Form>
          <Box display="flex" justifyContent="flex-end">
            <ButtonGroup groupVariant="spaced">
              <Button variant="subtle" size="sm" href={skillsListPath} isDisabled={saveMutation.isPending}>
                Cancel
              </Button>
              <Button
                variant="primary"
                size="sm"
                type="submit"
                form={formId}
                isDisabled={!canSave}
                isLoading={saveMutation.isPending}
              >
                Create
              </Button>
            </ButtonGroup>
          </Box>
        </Box>
      </Box>
    </AgentLayout>
  );
}
